import {PageHeading, Section, Wrapper, SubSection} from "./styled";
import basicScript from './basic_script.json'
import mcq_sample from './mcq_sample.json'
import parameterizedScript from './parameterized_script.json'
import AceEditor from "react-ace";
import React from "react";

export default function UserGuide() {
  return (
    <Wrapper>
      <PageHeading>User Guide</PageHeading>
      <div>
        With this tool, you can create dynamic WhatsApp surveys where you define the flow of questions based on responses,
        dynamically modify messages, sign up users for follow-ups, and much more using custom functions. <br/><br/>
        <b><i>How to use this guide?</i></b> If you're using the tool for the first time, we recommend starting with <b> Creating a basic Survey</b>.
        This hands-on guide will give you a feel for what the tool is all about. If you're already familiar with the basics,
        feel free to jump to the relevant sections below.
      </div>
      <ul>
        <li>
          <a href={'#tutorial'}>Tutorial</a>
          <ul>
            <li>
              <a href={'#tutorial-basic'}>Creating a basic survey</a>
            </li>
            <li>
              <a href={'#tutorial-JSON'}>Understanding the JSON script</a>
            </li>
            <li>
              <a href={'#tutorial-params'}>Adding parameters</a>
            </li>
            <li>
              <a href={'#tutorial-next-condition'}>Next state control</a>
            </li>
          </ul>
        </li>
      </ul>
      <Section id={'tutorial'}>
        <div style={{fontSize: '30px'}}>Tutorial</div>
        <SubSection id={'tutorial-basic'}>
          <div style={{fontSize: '20px',fontWeight: 'bold', fontStyle: 'italic'}}>Basic survey</div>
          <div>
            We'll start by creating a basic survey that asks for the user's name and sends a greeting message.
            Right click on <b>+ New Survey</b> on the left pane and open it in new tab. You'll see a form with some text
            boxes
            and a script editor. Let's understand what these text boxes mean:
            <br/><br/>
            <u>Survey name</u>: A unique identifier for the survey. This name must not already exist in the system, ensuring all surveys have distinct names. <i>Fill in a unique survey name</i>
            <br/><br/>
            <u>Instance name</u>: Instance is used to group together similar surveys. For example, a set of surveys
            meant to
            collect information from coffee planters in Colombia can have an instance name such as - 'Coffee planters'.
            <i> For now, fill in this text box with the value <b>test</b></i>
            <br/><br/>
            <u>Start message</u>: This is a unique message that the user sends to start the survey. <i>Fill in with a
            unique
            start message, something like <b>start &lt;survey name&gt; </b></i>
            <br/><br/>
            <u>Comments</u>: This should be a helpful description of the survey
            <br/><br/>
            <u>Script</u>: This is where the survey's flow of messages is defined in JSON format. You should see a sample JSON
            script.Delete the sample script and paste the following:
            <br/> <br/>
            <AceEditor
              mode="json"
              theme="monokai"
              name="deployment"
              readOnly={true}
              value={JSON.stringify(basicScript, null, 4)}
              fontSize={14}
              height={'370px'}
              width={'525px'}
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
            <br/>
            Now, click on the <b>Create Survey</b> button below the script editor. If your <b>Survey Name</b> and
            <b> Start Message</b> are unique, you’ll be redirected to the <b>Survey</b> page. Your newly created
            survey will be at the bottom of the list. Before moving on, let's test the survey you created.  <br/>
            <ul>
            <li>Take out your phone and open Whatsapp. Add a new contact: +1 (646) 217-0881, name it whatever you like.</li>
            <li>Send the <u>Start message</u> of the survey you created to this number.</li>
            <br/>
            </ul>
            This should start the survey and you should get a reply - "Hi, what is your name?". Reply with a random name like
            "John Doe". After sending this, you should receive 2 more messages - "Hello there!" and "Hope you are having a great day!"<br/>
            Voila! It's that easy to create a survey.
            <br/><br/>
            <b>Please note: </b> When you start a survey by sending its start message to the WhatsApp number, the system creates an active survey session.
            During this session, all messages you send are considered responses to that survey, and you cannot start another survey simultaneously.
            <br/>
            The session ends when you complete the survey or remain inactive for more than 24 hours. If you wish to exit a survey early, send "#bye" (without quotes).
            Once the session ends, you can start a new survey or restart the same one by sending the respective start message.
            </div>
            <SubSection id={'tutorial-JSON'}>
            <div style={{fontSize: '20px',fontWeight: 'bold'}}>Understanding the JSON script</div>
            <div>
            Before we dive into the structure of the script above, it is recommended that you
            familiarize yourself with JSON syntax. Here is a good resource: &nbsp;
            <a target={'_blank'} href={'https://www.digitalocean.com/community/tutorials/an-introduction-to-json'}>
              An Introduction to JSON
            </a>
            <br/><br/>
            Let's understand the JSON script now. On the left pane, click on <b>Surveys</b>, find your survey and click
            on it.
            This will take you
            to the edit page. Aside from the survey name, you can edit anything here. The JSON script is where you define
            the
            messages and the flow of the survey. Some of the key elements are:
            <br/><br/>
            <ul>
              <li>
                <b>restart_word</b>: The value of this key is used by the user to restart the survey while they are in an active session.<br/>
                You can try it out! Send the <u>Start message</u> of the survey to the whatsapp number. When
                you get the
                reply - "Hi, what is your name?", send <i>restart</i>. This would restart the survey and you'll see the
                first message again.
              </li>
              <li>
                <b>survey</b>: This holds the messages and their order in the form of states. The states are defined as
                key-value pairs.
                The entry point of the survey is defined by the key "start", which is mandatory for every
                survey.
              </li>
              <li>
                <b>start</b>: The value of this state is another object with key-value pairs:
                <ul>
                  <li>
                    <b>"type"</b>: specifies the type of response that we expect from the user. This can be of 3 types:
                    <ul>
                      <li>
                        "var": used to receive text responses from the user. eg.,a name.
                      </li>
                      <li>
                        "text":  used in the last state of the survey where we don't expect a response
                        from the user. For instance, we used this type in the "greet" state of the basic survey we created in the section above.
                      </li>
                      <li>
                        "mcq": used when we give users a multiple choice question and expect one of the option numbers as a
                        response. For instance:<br/>
                        <AceEditor
                                      mode="json"
                                      theme="monokai"
                                      name="deployment"
                                      readOnly={true}
                                      value={JSON.stringify(mcq_sample, null, 4)}
                                      fontSize={14}
                                      height={'250px'}
                                      width={'525px'}
                                      showPrintMargin={true}
                                      showGutter={true}
                                      highlightActiveLine={true}
                                      setOptions={{
                                        enableBasicAutocompletion: true,
                                        enableLiveAutocompletion: true,
                                        enableSnippets: false,
                                        showLineNumbers: true,
                                        tabSize: 2,
                                        useWorker: false,
                                      }}
                                    />
                                    <br/>

                      </li>
                    </ul>
                  </li>
                  <li>
                    <b>"msgs"</b>: This is an array of messages to be sent to the user. The system sends these messages
                    in
                    order with a pause of 1-2 seconds between them.
                  </li>
                  <li>
                    <b>"next"</b>: Here we specify which state to move to next after the user has responded to the msgs
                    in
                    the current state. In the example, we move to the "greet" state.<br/>
                    <ul>
                    <li><i> Please note that except the
                    "start"
                    state, the other states can be named anything you wish. For example, you can change the state name
                    from
                    "greet" to "bye bye"</i> </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <b>"greet"</b>: This is the last state of this survey. Notice that the "type" is <i>text</i>, which
                means
                that we don't expect any response from the user.
              </li>
            </ul>
          </div>
        </SubSection>
        <SubSection id={'tutorial-params'}>
          <div style={{fontSize: '20px', fontStyle: 'italic'}}>Adding parameters</div>
          <div>
            Parameters can make your messages dynamic. Let’s start by modifying the survey we created in the previous step.
            Copy the script below, go to the edit page of your survey, and paste it into the script editor.  Then, click <b>Save Changes</b>
            &nbsp; at the bottom of the page.
            <br/> <br/>
            <AceEditor
              mode="json"
              theme="monokai"
              name="deployment"
              readOnly={true}
              value={JSON.stringify(parameterizedScript, null, 4)}
              fontSize={14}
              height={'630px'}
              width={'625px'}
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
            <br/>
            Test the changes using WhatsApp. Once you've saved the changes, the updates will be applied immediately.
            Send the start message and complete the survey. You’ll notice that the final message greets you by name and includes today’s date.
            This level of dynamism is made possible through the use of <b>Parameters</b>.
            <br/>
            <br/>
            We’ve added two parameters to the survey:<i> %%name%% </i> and <i>%%day%%</i> in the "msgs" of the "greet" state.
            Anything between two percent signs (e.g., %%parameter%%) is considered a parameter and will be dynamically substituted while the user is taking the survey.
            We need to define how the values for these parameters are determined using the <b>params</b> object. Here’s how it works for our two parameters:


            <ul>
              <li>
                <b>"name"</b>: The name parameter should be substituted with the user's response from the previous state
                (i.e., the "start" state, where the user entered their name). This is handled by the <span style={{backgroundColor: '#d0d0d0', padding: '2px 6px',
                borderRadius: '4px'}}>get_from_response</span> function,
                with the argument "start" to fetch the user’s response from that state. You can use various other functions to retrieve data from different states.
                Check the <a href={'#'}>functions reference</a> for details on available functions and their supported arguments.
              </li>
              <li>
                <b>"day"</b>: The day parameter should be substituted with the current day when the user takes the survey.
                To get this, we use the <span style={{backgroundColor: '#d0d0d0', padding: '2px 6px',borderRadius: '4px'}}> get_current_date</span> function.
                This function accepts the date format as "args". Here, we have
                used "%A" which returns the week day. You can replace %A with any valid format
                (see <a href={'https://strftime.org/'} target={'_blank'}>strftime.org</a> for more options).<br/>
                For example: change the args from <b>"%A"</b> to
                &nbsp;<b>"%m-%d-%Y"</b> and save the changes.When you test the survey on WhatsApp,you'll see that the last message
                greets you with the full date.
              </li>
            </ul>

          </div>
        </SubSection>
        <SubSection id={'tutorial-next-condition'}>
          <div style={{fontSize: '20px', fontStyle: 'italic'}}>Next state control</div>
          <div>
            There are use cases where you might want to decide the next state based on some condition. For instance, let's
            say we have a survey that collects information about the weather conditions during planting season. We wouldn't
            want to collect the weather info if the user is out of the planting season. To be more clear, let's say the
            planting season for a crop is May - July. If a farmer takes the survey during these months, we would want to
            ask them the average amount of rainfall. But, if a farmer starts the survey in the month of December, we are not
            really interested in the weather conditions. So, we'd skip that question and ask the farmer to take the survey
            during planting season.
            <br/><br/>
            Another interesting use case is when we want to move to a state based on the user's response in one of the previous
            states. For example, let's say we have a survey that asks user their language preference. Depending on their response,
            we can then direct them to a state where all the survey messages are in the selected language.

            <br/><br/>
            We can implement these two use cases as follows. Edit the survey you created earlier by replacing the existing script with the one below:
            <br/> <br/>
            <AceEditor
              mode="json"
              theme="monokai"
              name="deployment"
              readOnly={true}
              value={JSON.stringify(parameterizedScript, null, 4)}
              fontSize={14}
              height={'630px'}
              width={'625px'}
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
            <br/>
            Test it out!
          </div>
        </SubSection>
        </SubSection>
      </Section>
    </Wrapper>
  )
}